import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Typography,
  TablePagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Router as RouterIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Model, Brand } from '../../types';
import equipmentService from '../../services/equipmentService';

interface ModelsManagerModalProps {
  open: boolean;
  onClose: () => void;
  onEditModel: (model: Model) => void;
  onDeleteModel: (model: Model) => void;
  onCreateModel: () => void;
  brands: Brand[];
}

interface ModelFilters {
  marcaId?: number;
  tipo?: string;
  frequencia_wifi?: string;
  portas_lan?: number;
  portas_fxs?: number;
  search?: string;
}

const ModelsManagerModal: React.FC<ModelsManagerModalProps> = ({
  open,
  onClose,
  onEditModel,
  onDeleteModel,
  onCreateModel,
  brands
}) => {
  const [models, setModels] = useState<Model[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState<ModelFilters>({
    marcaId: undefined,
    tipo: '',
    frequencia_wifi: '',
    portas_lan: undefined,
    portas_fxs: undefined,
    search: ''
  });

  const tiposEquipamento = [
    { value: '', label: 'Todos os tipos' },
    { value: 'onu', label: 'ONU' },
    { value: 'ont', label: 'ONT' },
    { value: 'roteador', label: 'Roteador' },
    { value: 'switch', label: 'Switch' }
  ];

  const frequenciasWifi = [
    { value: '', label: 'Todas as frequências' },
    { value: '2.4GHz', label: '2.4GHz' },
    { value: '5GHz', label: '5GHz' },
    { value: '6GHz', label: '6GHz (WiFi 6E)' },
    { value: 'Dual Band', label: 'Dual Band' },
    { value: 'Tri Band', label: 'Tri Band' }
  ];

  // Carregar modelos quando o modal abrir
  useEffect(() => {
    if (open) {
      loadModels();
      setPage(0); // Reset página ao abrir
    }
  }, [open]);

  // Aplicar filtros quando mudarem
  useEffect(() => {
    applyFilters();
  }, [models, filters]);

  const loadModels = async () => {
    setLoading(true);
    try {
      const response = await equipmentService.getModels();
      if (response.success && response.data) {
        setModels(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...models];

    // Filtro por marca
    if (filters.marcaId) {
      filtered = filtered.filter(model => 
        (model.marcaId === filters.marcaId || model.marcaId === filters.marcaId)
      );
    }

    // Filtro por tipo
    if (filters.tipo) {
      filtered = filtered.filter(model => model.tipo === filters.tipo);
    }

    // Filtro por frequência WiFi
    if (filters.frequencia_wifi) {
      filtered = filtered.filter(model => 
        model.frequencia_wifi && model.frequencia_wifi.includes(filters.frequencia_wifi!)
      );
    }

    // Filtro por quantidade de portas LAN
    if (filters.portas_lan) {
      filtered = filtered.filter(model => 
        (model.portas_lan === filters.portas_lan || model.portas_lan === filters.portas_lan)
      );
    }

    // Filtro por quantidade de portas FXS
    if (filters.portas_fxs) {
      filtered = filtered.filter(model => 
        (model.portas_fxs === filters.portas_fxs || model.portas_fxs === filters.portas_fxs)
      );
    }

    // Filtro por busca textual
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(model =>
        model.titulo.toLowerCase().includes(searchLower) ||
        model.marca?.titulo.toLowerCase().includes(searchLower) ||
        model.tipo.toLowerCase().includes(searchLower)
      );
    }

    setFilteredModels(filtered);
    setPage(0); // Reset página quando filtros mudarem
  };

  const handleFilterChange = (field: keyof ModelFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      marcaId: undefined,
      tipo: '',
      frequencia_wifi: '',
      portas_lan: undefined,
      portas_fxs: undefined,
      search: ''
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTipoLabel = (tipo: string) => {
    const tipoObj = tiposEquipamento.find(t => t.value === tipo);
    return tipoObj ? tipoObj.label : tipo.toUpperCase();
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => 
      value !== undefined && value !== '' && value !== null
    ).length;
  };

  // Dados paginados
  const paginatedModels = filteredModels.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Gerenciar Modelos ({filteredModels.length})
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              color={getActiveFiltersCount() > 0 ? 'primary' : 'inherit'}
            >
              Filtros {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onCreateModel}
              disabled={brands.length === 0}
            >
              Novo Modelo
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {brands.length === 0 ? (
          <Alert severity="warning">
            Cadastre pelo menos uma marca antes de criar modelos.
          </Alert>
        ) : (
          <>
            {/* Filtros */}
            {showFilters && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">Filtros</Typography>
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    disabled={getActiveFiltersCount() === 0}
                  >
                    Limpar Filtros
                  </Button>
                </Box>
                
                <Grid container spacing={2}>
                  {/* Busca */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Buscar"
                      value={filters.search || ''}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Nome do modelo, marca..."
                    />
                  </Grid>

                  {/* Marca */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Marca</InputLabel>
                      <Select
                        value={filters.marcaId || ''}
                        onChange={(e) => handleFilterChange('marcaId', e.target.value)}
                        label="Marca"
                      >
                        <MenuItem value="">Todas as marcas</MenuItem>
                        {brands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.titulo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tipo */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={filters.tipo || ''}
                        onChange={(e) => handleFilterChange('tipo', e.target.value)}
                        label="Tipo"
                      >
                        {tiposEquipamento.map((tipo) => (
                          <MenuItem key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Frequência WiFi */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Frequência WiFi</InputLabel>
                      <Select
                        value={filters.frequencia_wifi || ''}
                        onChange={(e) => handleFilterChange('frequencia_wifi', e.target.value)}
                        label="Frequência WiFi"
                      >
                        {frequenciasWifi.map((freq) => (
                          <MenuItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Portas LAN */}
                  <Grid item xs={12} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Portas LAN"
                      value={filters.portas_lan || ''}
                      onChange={(e) => handleFilterChange('portas_lan', e.target.value ? parseInt(e.target.value) : undefined)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  {/* Portas FXS */}
                  <Grid item xs={12} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Portas FXS"
                      value={filters.portas_fxs || ''}
                      onChange={(e) => handleFilterChange('portas_fxs', e.target.value ? parseInt(e.target.value) : undefined)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Tabela */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <Typography>Carregando modelos...</Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Imagem</TableCell>
                        <TableCell>Marca</TableCell>
                        <TableCell>Modelo</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Portas LAN</TableCell>
                        <TableCell>Portas FXS</TableCell>
                        <TableCell>Frequência WiFi</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedModels.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography color="textSecondary">
                              {filteredModels.length === 0 && models.length > 0 
                                ? 'Nenhum modelo encontrado com os filtros aplicados'
                                : 'Nenhum modelo cadastrado'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedModels.map((model) => (
                          <TableRow key={model.id} hover>
                            <TableCell>
                              <Avatar
                                src={model.imagem ? equipmentService.getModelImageUrl(model.imagem) : undefined}
                                sx={{ width: 40, height: 40 }}
                              >
                                <RouterIcon />
                              </Avatar>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {model.marca?.titulo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {model.titulo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={getTipoLabel(model.tipo)} 
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {model.portas_lan || model.portas_lan || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {model.portas_fxs || model.portas_fxs || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {model.frequencia_wifi ? (
                                <Chip 
                                  label={model.frequencia_wifi} 
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => onEditModel(model)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDeleteModel(model)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paginação */}
                <TablePagination
                  component="div"
                  count={filteredModels.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelsManagerModal; 